import React, { useState } from 'react';
import { FiLogOut, FiUser, FiAward, FiSun, FiMoon, FiMenu, FiUserPlus } from 'react-icons/fi';
import { IoShieldCheckmarkSharp } from 'react-icons/io5';
import { User } from '../../types';
import { useModalStore } from '../../stores';
import { useTheme } from '../../hooks/useTheme';
import { getProfileImageUrl, DEFAULT_AVATAR_URL } from '../../utils/imageUtils';
import AdminApprovalModal from '../Admin/AdminApprovalModal';
import MasterManagementModal from '../Admin/MasterManagementModal';
import NotificationDropdown from '../Community/NotificationDropdown';

interface HeaderProps {
  user: User;
  onLogout: () => void;
  onToggleSidebar?: () => void;
  showSidebarToggle?: boolean;
}

const Header: React.FC<HeaderProps> = ({
  user,
  onLogout,
  onToggleSidebar,
  showSidebarToggle = false,
}) => {
  const { openModal } = useModalStore();
  const { theme, toggleTheme } = useTheme();
  const [showMasterModal, setShowMasterModal] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  return (
    <>
      <header
        className={`${showSidebarToggle ? 'app-navbar' : ''} app-topbar relative sticky top-0 z-40 border-b border-white/10`}
      >
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-[#246BF5]/50 to-transparent" />
        <div className="relative mx-auto w-full px-3 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2 sm:gap-3">
              {showSidebarToggle && (
                <button
                  onClick={onToggleSidebar}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-[#246BF5]/20 bg-white/40 text-foreground shadow-[0_0_16px_rgba(36,107,245,0.08)] backdrop-blur-md transition-all hover:border-[#246BF5]/40 hover:text-primary dark:bg-white/5 lg:hidden"
                  aria-label="Toggle navigation"
                >
                  <FiMenu className="h-5 w-5" />
                </button>
              )}

              <div className={`flex items-center gap-2 ${showSidebarToggle ? 'lg:hidden' : ''}`}>
                <div className="relative">
                  <div className="absolute inset-0 rounded-full bg-[#246BF5]/25 blur-md" />
                  <img
                    src="/trade-pilot-logo.png"
                    alt="Trade Pilot"
                    className="relative h-9 w-9 object-cover lg:h-[52px] lg:w-[52px]"
                  />
                </div>
                <div>
                  <h1 className="title-brand-gradient whitespace-nowrap text-base sm:text-lg lg:text-2xl">Trade Pilot</h1>
                </div>
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-1.5 sm:gap-3">
              {user.role === 'admin' && (
                <button
                  onClick={() => setShowMasterModal(true)}
                  className="hidden items-center gap-2 rounded-xl border border-[#246BF5]/25 bg-white/50 px-4 py-2 text-sm font-semibold shadow-[0_0_18px_rgba(36,107,245,0.1)] backdrop-blur-md transition-all hover:border-[#246BF5]/50 hover:text-primary dark:bg-white/5 sm:flex"
                >
                  <FiUserPlus className="h-4 w-4" />
                  <span>Add Master</span>
                </button>
              )}

              <NotificationDropdown />

              <button
                onClick={toggleTheme}
                className="relative flex h-10 w-10 items-center justify-center rounded-xl border border-[#246BF5]/20 bg-white/45 text-foreground shadow-[0_0_16px_rgba(36,107,245,0.08)] backdrop-blur-md transition-all hover:border-[#246BF5]/40 hover:text-primary dark:bg-white/5"
                title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {theme === 'dark' ? (
                  <FiSun className="h-5 w-5 text-warning" />
                ) : (
                  <FiMoon className="h-5 w-5" />
                )}
              </button>

              <div className="hidden items-center gap-2.5 rounded-xl border border-[#246BF5]/20 bg-white/45 px-3 py-2 shadow-[0_0_20px_rgba(36,107,245,0.1)] backdrop-blur-md transition-all duration-200 hover:border-[#246BF5]/40 dark:bg-white/[0.06] sm:flex">
                {user.profileImage ? (
                  <div className="h-8 w-8 overflow-hidden rounded-full border-2 border-[#246BF5]/30 bg-surface shadow-[0_0_12px_rgba(36,107,245,0.25)]">
                    <img
                      src={getProfileImageUrl(user.profileImage)}
                      alt={user.name}
                      crossOrigin="anonymous"
                      className="h-full w-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = DEFAULT_AVATAR_URL;
                      }}
                    />
                  </div>
                ) : (
                  <div
                    className={`flex items-center justify-center rounded-full ring-2 ring-[#246BF5]/35 ${
                      user.role === 'master'
                        ? 'h-9 w-9 bg-gradient-to-br from-[#246BF5] to-[#1d5ad4]'
                        : user.role === 'admin'
                          ? 'h-9 w-9 bg-gradient-to-br from-primary-500 to-primary-600 shadow-sm'
                          : 'h-9 w-9 bg-gradient-to-br from-gray-400 to-gray-500'
                    }`}
                  >
                    {user.role === 'master' ? (
                      <FiAward className="h-4 w-4 text-white" />
                    ) : user.role === 'admin' ? (
                      <IoShieldCheckmarkSharp className="h-5 w-5 text-white" />
                    ) : (
                      <FiUser className="h-4 w-4 text-white" />
                    )}
                  </div>
                )}
                <div>
                  <p className="text-sm font-semibold leading-none text-foreground">{user.name}</p>
                  <p className="mt-0.5 text-xs capitalize text-muted">{user.role}</p>
                </div>
              </div>

              <button
                onClick={() => setShowLogoutModal(true)}
                className="btn-ghost flex items-center gap-2 rounded-xl border border-transparent px-3 py-2 text-sm transition-all hover:border-[#246BF5]/25 hover:bg-white/30 dark:hover:bg-white/5"
                title="Logout"
              >
                <FiLogOut className="h-4 w-4" />
                <span className="hidden md:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>
      <AdminApprovalModal />
      {user.role === 'admin' && (
        <MasterManagementModal
          isOpen={showMasterModal}
          onClose={() => setShowMasterModal(false)}
        />
      )}
      {showLogoutModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">Confirm Logout</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to logout?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowLogoutModal(false)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={onLogout}
                className="px-4 py-2 bg-danger hover:opacity-90 text-white rounded-lg font-semibold transition-colors"
              >
                Confirm Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
